import { Box, LinearProgress, LinearProgressProps, Stack, Tooltip, Typography, TypographyProps } from "@mui/material";
import React from "react";

import CachedIcon from "@mui/icons-material/Cached";
import CheckIcon from "@mui/icons-material/Check";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import AirportShuttleIcon from "@mui/icons-material/AirportShuttle";
import PendingIcon from "@mui/icons-material/Pending";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import ApprovalIcon from "@mui/icons-material/Approval";
import HelpIcon from "@mui/icons-material/Help";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { GetOrderStatusLabel } from "../../../Utils/helpers";
import { useTranslation } from "react-i18next";

export type TableCellOrderStatusProps = {
  data: any;
  showProgressBar?: boolean;
  hideText?: boolean;
};

const TableCellOrderStatus: React.FC<TableCellOrderStatusProps> = (props) => {
  const { t } = useTranslation();
  const value = props.data === null || props.data === undefined ? "" : props.data.value;
  const showText = !props.hideText;
  let color: TypographyProps["color"];
  let progressBarColor: LinearProgressProps["color"];
  let text = GetOrderStatusLabel(value);
  let icon;

  // TODO: move switch logic do a misc component
  switch (value) {
    case "RUNNING":
      color = "primary.text";
      progressBarColor = "primary";
      icon = <CachedIcon />;
      break;
    case "READY":
      color = "text.tier";
      progressBarColor = "info";
      icon = <AlarmOnIcon />;
      break;
    case "QUEUED":
      color = "text.tier";
      progressBarColor = "info";
      icon = <HourglassEmptyIcon />;
      break;
    case "CREATED":
      color = "text.tier";
      progressBarColor = "primary";
      icon = <FiberNewIcon />;
      break;
    case "COMPLETED":
      color = "success.main";
      progressBarColor = "success";
      icon = <CheckIcon />;
      break;
    case "FAILED":
      color = "warning.main";
      progressBarColor = "warning";
      icon = <SettingsOutlinedIcon />;
      break;
    case "ABORTED":
      color = "warning.main";
      progressBarColor = "warning";
      icon = <SettingsOutlinedIcon />;
      break;
    case "CANCELED":
      color = "warning.main";
      progressBarColor = "warning";
      icon = <CancelPresentationIcon />;
      break;
    case "MANUAL_DELIVERY_BEFORE_COMPLETED":
      color = "info.main";
      progressBarColor = "info";
      icon = <AirportShuttleIcon />;
      break;
    case "WAITING_APPROVAL":
      color = "info.main";
      progressBarColor = "info";
      icon = <ApprovalIcon />;
      break;
    case "QUOTE_PROPOSED":
      color = "info.main";
      icon = <HelpIcon />;
      break;
    case "QUOTE_REQUESTED":
      color = "primary.text";
      icon = <EmojiPeopleIcon />;
      break;
    case "QUOTE_ACCEPTED":
      color = "success.main";
      icon = <HowToRegIcon />;
      break;
    case "QUOTE_DECLINED":
      color = "warning.main";
      icon = <ThumbDownAltIcon />;
      break;
    case "SCHEDULED":
      color = "info.main";
      progressBarColor = "info";
      icon = <EventAvailableIcon />;
      break;
    default:
      color = undefined;
      progressBarColor = "primary";
      icon = <PendingIcon />;
      break;
  }

  return (
    <Stack>
      <Stack direction="row" alignItems="center" spacing={0.5} color={color}>
        <Tooltip title={text}>{icon}</Tooltip>
        {showText && (
          <Typography sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>{text}</Typography>
        )}
        {props.showProgressBar && (
          <Box sx={(theme) => ({ width: "60px", flexShrink: 0, ml: `${theme.spacing(1)} !important` })}>
            <LinearProgress sx={{ borderRadius: "1px" }} color={progressBarColor} variant="determinate" value={30} />
          </Box>
        )}
      </Stack>
      {props.data !== null &&
      props.data !== undefined &&
      props.data.row !== undefined &&
      props.data.row["itemsCompleted"] !== undefined &&
      props.data.row["itemsTotal"] !== undefined ? (
        <Typography variant="body2" color="text.secondary">
          {props.data.row["itemsCompleted"]} / {props.data.row["itemsTotal"]} {t("table:body.jobCompleted")}
        </Typography>
      ) : (
        <></>
      )}
    </Stack>
  );
};

export default TableCellOrderStatus;
